import Swiper, { Autoplay, Navigation, Scrollbar } from 'swiper';
import {swiperObserver} from '../../utils/swiper.utils.js';
import { swiperOnResize } from '../../utils/swiperOnResize.utils.js';

const initializedWrappers = new WeakSet();
const equalizersRegistry = new WeakMap();
const rowTopTolerance = 4;
const equalizedSelectors = [
    '.job-card__meta',
    '.job-card__title',
    '.job-card__details',
];

const getVacanciesSlides = (swiperEl) => {
    return Array.from(
        swiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide')
    );
};

const getVacanciesSlideRows = (swiperEl) => {
    const slides = getVacanciesSlides(swiperEl);
    const swiper = swiperEl.swiper;

    if (!slides.length) {
        return [];
    }

    if (swiper && !swiper.destroyed) {
        return [slides];
    }

    const rows = [];

    slides.forEach((slide) => {
        const top = slide.getBoundingClientRect().top;
        const existingRow = rows.find((row) => {
            return Math.abs(row.top - top) <= rowTopTolerance;
        });

        if (existingRow) {
            existingRow.slides.push(slide);
            existingRow.top = Math.min(existingRow.top, top);
            return;
        }

        rows.push({
            top,
            slides: [slide],
        });
    });

    return rows
        .sort((a, b) => a.top - b.top)
        .map((row) => row.slides);
};

const getVacanciesEqualizerGroups = (swiperEl) => {
    const rows = getVacanciesSlideRows(swiperEl);
    const groups = [];

    rows.forEach((row) => {
        equalizedSelectors.forEach((selector) => {
            const elements = row
                .map((slide) => slide.querySelector(selector))
                .filter(Boolean);

            if (elements.length) {
                groups.push(elements);
            }
        });
    });

    return groups;
};

const setupVacanciesEqualizer = (swiperEl) => {
    const equalizer = equalizersRegistry.get(swiperEl);

    if (equalizer) {
        equalizer.refresh();
        return;
    }

    if (typeof window.lineTitlesFunc !== 'function') {
        return;
    }

    equalizersRegistry.set(
        swiperEl,
        window.lineTitlesFunc([], {
            getGroups: () => getVacanciesEqualizerGroups(swiperEl),
        })
    );
};

const initVacanciesSwiper = (scope = document) => {
    const vacanciesSwiperWr = scope.querySelectorAll('.vacancies-swiper-wr');

    vacanciesSwiperWr.forEach((el) => {
        if (initializedWrappers.has(el)) {
            const swiperEl = el.querySelector('.swiper');

            if (swiperEl) {
                setupVacanciesEqualizer(swiperEl);
            }

            return;
        }

        const swiperEl = el.querySelector('.swiper');

        if (!swiperEl) {
            return;
        }

        const scrollbarEl = el.querySelector('.swiper-scrollbar');
        const slidesCount = getVacanciesSlides(swiperEl).length;

        if (!slidesCount) {
            return;
        }

        initializedWrappers.add(el);

        const onResizeType = swiperEl.classList.contains('on-resize-type');
        const isJobCardType = el.closest('.vacancies-sec')?.classList.contains('job-card-type');
        const nextEl = el.querySelector('.swiper-button-next');
        const prevEl = el.querySelector('.swiper-button-prev');

        if (isJobCardType) {
            swiperOnResize('(max-width: 1023px)', swiperEl,
                {
                    modules: [Autoplay, Navigation],
                    observer: true,
                    observeParents: true,
                    slidesPerView: 1,
                    spaceBetween: 20,
                    threshold: 10,
                    navigation: nextEl && prevEl ? {
                        nextEl,
                        prevEl,
                    } : undefined,
                    breakpoints: {
                        768: {
                            slidesPerView: 2,
                            spaceBetween: 24,
                        },
                    },
                    autoplay: slidesCount > 1 ? {
                        delay: 4000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: false,
                    } : false,
                },
                true,
            );
        } else if (slidesCount > 3 && !onResizeType) {
            if (swiperEl.swiper && !swiperEl.swiper.destroyed) {
                swiperEl.swiper.update();
            } else {
                const swiper = new Swiper(swiperEl, {
                    modules: [Autoplay, Scrollbar],
                    observer: true,
                    observeParents: true,
                    spaceBetween: 19,
                    slidesPerView: 1,
                    threshold: 10,
                    scrollbar: scrollbarEl ? {
                        el: scrollbarEl,
                        draggable: true,
                        dragSize: 'auto',
                    } : undefined,
                    breakpoints: {
                        552: {
                            spaceBetween: 20,
                            slidesPerView: 2,
                        },
                        740: {
                            spaceBetween: 26,
                            slidesPerView: 3,
                        },
                        1260: {
                            spaceBetween: 41,
                            slidesPerView: 3,
                        },
                    },
                    autoplay: {
                        delay: 4000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: window.innerWidth >= 1033,
                    }
                });

                swiperObserver(swiper);
            }
        } else if (onResizeType && slidesCount > 3) {
            swiperOnResize('(max-width: 1023px)', swiperEl,
                {
                    modules: [Autoplay, Scrollbar],
                    slidesPerView: 1,
                    spaceBetween: 19,
                    autoplay: {
                        delay: 4000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: false,
                    },
                    breakpoints: {
                        552: {
                            spaceBetween: 20,
                            slidesPerView: 2,
                        },
                        740: {
                            spaceBetween: 26,
                            slidesPerView: 2,
                        },
                        951: {
                            spaceBetween: 16,
                            slidesPerView: 3,
                        },
                        1260: {
                            spaceBetween: 41,
                            slidesPerView: 3,
                        },
                    },
                    scrollbar: scrollbarEl ? {
                        el: scrollbarEl,
                        draggable: true,
                        dragSize: 'auto',
                    } : undefined,
                },
            );
        } else {
            swiperEl.classList.add('no-swiper-type');
        }

        setupVacanciesEqualizer(swiperEl);
    });
};

initVacanciesSwiper();
window.initVacanciesSwiper = initVacanciesSwiper;
